import { fromJS, Map } from 'immutable';
import { Delta, PushEvent } from '../../../dto/PushEnvelope';
import { Bet } from '../../../dto';
import { indexById, updateIndexedById } from '../../indexingUtils';
import fp from 'lodash/fp';

export function onBetsSnapshotMerge(marketId: string, bets: Bet[]) {
    return (db: Map<string, any>) => {
        return db
            .mergeIn(['bets'], indexById(bets))
            .mergeIn(['betIdsByMarketAndMatch', marketId], indexBetsByMatch(bets));
    };
}

export function onBetPush(event: PushEvent, data: Bet | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onCreated(data as Bet);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'bets');
        case PushEvent.DELETED:
            return onDeleted(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
}

function onCreated(bet: Bet) {
    return turboLiveDb => {
        const betId = bet.id;
        const matchId = bet.matchId;
        const marketId = bet.marketId;

        return turboLiveDb
            .update('bets', Map(), bets => bets.set(betId, fromJS(bet)))
            .update('betIdsByMarketAndMatch', Map(), marketIds =>
                marketIds.update(marketId, Map(), matchIds => matchIds.set(matchId, betId))
            );
    };
}

function onDeleted(betId: string) {
    return turboLiveDb => {
        const bet = turboLiveDb.get('bets').get(betId);
        if (!bet) {
            console.warn(`Bet ${betId} not found, cannot handle DELETE push event.`, { turboLiveDb });
            return turboLiveDb;
        }
        const marketId = bet.get('marketId');
        const matchId = bet.get('matchId');

        return turboLiveDb
            .update('bets', Map(), bets => bets.delete(betId))
            .update('betIdsByMarketAndMatch', Map(), marketIds =>
                marketIds.update(marketId, Map(), matchIds =>
                    matchIds.update(matchId, oldBetId => (oldBetId === betId ? null : oldBetId))
                )
            );
    };
}

export function indexBetsByMatch(bets: Bet[]) {
    const composeBets = fp.compose(
        fp.mapValues(matchBets => matchBets.map(bet => bet.id).shift()),
        fp.groupBy('matchId')
    );

    return fromJS(composeBets(bets));
}
